import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, ExternalLink, MoreVertical, Pencil, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge, PriorityBadge, BucketBadge, SectionBadge, Pill } from "@/components/Badges";
import { api, formatDate, bucketLabel } from "@/lib/api";
import { openDocument } from "@/lib/openDocument";
import { cn } from "@/lib/utils";

const RowActions = ({ task, onEdit, onComplete, onRefresh }) => {
  const remove = async () => {
    if (!window.confirm(`"${task.title}" kaydı silinsin mi?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Kayıt silindi.");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi.");
    }
  };

  const advance = async () => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: "Devam Ediyor" });
      toast.success("Durum güncellendi.");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Güncellenemedi.");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`task-menu-${task.id}`} aria-label="İşlemler">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl">
        {task.status !== "Tamamlandı" && (
          <DropdownMenuItem onClick={() => onComplete(task)} data-testid={`task-complete-${task.id}`}>
            <Check className="mr-2 h-4 w-4" /> Tamamlandı
          </DropdownMenuItem>
        )}
        {task.status === "Yapılacak" && (
          <DropdownMenuItem onClick={advance} data-testid={`task-progress-${task.id}`}>
            <CalendarClock className="mr-2 h-4 w-4" /> Devam Ediyor
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onEdit(task)} data-testid={`task-edit-${task.id}`}>
          <Pencil className="mr-2 h-4 w-4" /> Düzenle
        </DropdownMenuItem>
        {(task.drive_link || task.drive_file_id) && (
          <DropdownMenuItem
            onClick={() => openDocument({ taskId: task.id })}
            data-testid={`task-view-document-${task.id}`}
          >
            <ExternalLink className="mr-2 h-4 w-4" /> Evrakı Görüntüle
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={remove} className="text-crimson" data-testid={`task-delete-${task.id}`}>
          <Trash2 className="mr-2 h-4 w-4" /> Sil
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Flags = ({ task }) => (
  <>
    {task.deadline_needs_check && (
      <Pill className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
        Süre kontrolü gerekli
      </Pill>
    )}
    {task.folder_not_found && (
      <Pill className="border-crimson/25 bg-crimson/10 text-crimson dark:text-red-300">
        Dosya klasörü bulunamadı
      </Pill>
    )}
  </>
);

export const TaskList = ({
  tasks, loading, onEdit, onComplete, onRefresh, compact = false,
  emptyText = "Kayıt bulunmuyor.",
}) => {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="task-list-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[76px] animate-pulse rounded-xl border border-border bg-card" />
        ))}
      </div>
    );
  }

  if (!tasks.length) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center shadow-shb" data-testid="task-list-empty">
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      </div>
    );
  }

  const Cards = (
    <div className="space-y-3">
      {tasks.map((task, i) => (
        <motion.div
          key={task.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i * 0.03, 0.25) }}
          data-testid={`task-card-${task.id}`}
          className={cn(
            "rounded-xl border bg-card p-4 shadow-shb",
            task.bucket === "overdue" && task.status !== "Tamamlandı"
              ? "border-crimson/40"
              : "border-border"
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold leading-snug text-foreground">{task.title}</p>
              {task.description && (
                <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>
              )}
            </div>
            <RowActions task={task} onEdit={onEdit} onComplete={onComplete} onRefresh={onRefresh} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <SectionBadge section={task.section} />
            <StatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} />
            {task.status !== "Tamamlandı" && (
              <BucketBadge bucket={task.bucket} label={bucketLabel[task.bucket]} />
            )}
            <Flags task={task} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              Son tarih:{" "}
              <span className={cn("font-mono", task.bucket === "overdue" && task.status !== "Tamamlandı" && "font-semibold text-crimson")}>
                {formatDate(task.due_date)}
              </span>
            </span>
            <span>{task.record_type}</span>
            {task.assignee && <span>{task.assignee}</span>}
          </div>
        </motion.div>
      ))}
    </div>
  );

  if (compact) return <div data-testid="task-list">{Cards}</div>;

  return (
    <div data-testid="task-list">
      <div className="lg:hidden">{Cards}</div>

      <div className="hidden overflow-hidden rounded-xl border border-border bg-card shadow-shb lg:block">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[36%] text-[12px] font-semibold uppercase tracking-[0.6px]">Kayıt</TableHead>
              <TableHead className="text-[12px] font-semibold uppercase tracking-[0.6px]">Bölüm</TableHead>
              <TableHead className="text-[12px] font-semibold uppercase tracking-[0.6px]">Durum</TableHead>
              <TableHead className="text-[12px] font-semibold uppercase tracking-[0.6px]">Öncelik</TableHead>
              <TableHead className="text-[12px] font-semibold uppercase tracking-[0.6px]">Sorumlu</TableHead>
              <TableHead className="text-[12px] font-semibold uppercase tracking-[0.6px]">Son Tarih</TableHead>
              <TableHead className="text-right text-[12px] font-semibold uppercase tracking-[0.6px]">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.id} data-testid={`task-row-${task.id}`} className="transition-colors duration-150">
                <TableCell>
                  <p className="font-semibold text-foreground">{task.title}</p>
                  {task.description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{task.description}</p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{task.record_type}</span>
                    <Flags task={task} />
                  </div>
                </TableCell>
                <TableCell><SectionBadge section={task.section} /></TableCell>
                <TableCell><StatusBadge status={task.status} /></TableCell>
                <TableCell><PriorityBadge priority={task.priority} /></TableCell>
                <TableCell className="text-sm text-muted-foreground">{task.assignee || "—"}</TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <span className={cn("font-mono text-sm tabular-nums", task.bucket === "overdue" && task.status !== "Tamamlandı" && "font-semibold text-crimson")}>
                      {formatDate(task.due_date)}
                    </span>
                    {task.status !== "Tamamlandı" && task.bucket !== "later" && (
                      <BucketBadge bucket={task.bucket} label={bucketLabel[task.bucket]} />
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <RowActions task={task} onEdit={onEdit} onComplete={onComplete} onRefresh={onRefresh} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
